import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShieldBan, Trash2, RefreshCw } from 'lucide-react';

export default function EmailBlacklistPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState({});

  const fetchBlacklist = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getEmailBlacklist();
      setEntries(data || []);
    } catch (err) {
      console.error('Failed to fetch email blacklist:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBlacklist(); }, [fetchBlacklist]);

  const handleRemove = async (entryId) => {
    setRemoving(prev => ({ ...prev, [entryId]: true }));
    try {
      await api.removeFromBlacklist(entryId);
      setEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (err) {
      alert(`Fehler: ${err.message}`);
    } finally {
      setRemoving(prev => ({ ...prev, [entryId]: false }));
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6" data-testid="email-blacklist-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldBan className="w-6 h-6" />
            E-Mail Blacklist
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Globale Liste von E-Mail-Adressen die bei Zustellung fehlgeschlagen sind (Hard-Fail).
            Agents ueberspringen diese Adressen automatisch. Eintraege verfallen nach 30 Tagen.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchBlacklist} disabled={loading}
          data-testid="refresh-blacklist-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {entries.length} aktive Eintraege
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Laden...</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine Eintraege. Alle E-Mail-Adressen sind freigegeben.</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-3 py-1 border-b">
                <div className="col-span-4">E-Mail-Adresse</div>
                <div className="col-span-3">Grund</div>
                <div className="col-span-3">Blacklisted seit</div>
                <div className="col-span-2 text-right">Aktion</div>
              </div>
              {entries.map(entry => (
                <div key={entry.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 rounded hover:bg-muted/50 text-sm"
                  data-testid={`blacklist-entry-${entry.id}`}>
                  <div className="col-span-4 font-mono text-xs truncate" title={entry.email_address}>
                    {entry.email_address}
                  </div>
                  <div className="col-span-3 text-muted-foreground text-xs">
                    {entry.reason || '-'}
                  </div>
                  <div className="col-span-3 text-muted-foreground text-xs">
                    {formatDate(entry.created_at)}
                  </div>
                  <div className="col-span-2 text-right">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleRemove(entry.id)}
                      disabled={removing[entry.id]}
                      title="Von Blacklist entfernen"
                      data-testid={`remove-blacklist-${entry.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
